import { useEffect } from 'react';
import { t } from 'i18next';
import { getUser } from '@frontend/auth-helpers';
import { Icon } from '@frontend/icons';
import { useNotificationSettings } from '@frontend/notifications';
import { useShellTheme } from '@frontend/shell-theme';
import { shell } from '@frontend/shell-utils';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, Heading, Text, useVerticalSlideModal } from '@frontend/design-system';
import { ShellThemeSwitcher } from '../shell-theme-switcher';
import { saveShellThemeToLocalStorage } from '../shell-theme-switcher/helper';
import fourSecondDismissalVideo from './assets/4SecondDismissal.mp4';
import displayUntilDismissedVideo from './assets/displayUntilDismissed.mp4';
import { isCompactViewOnTop } from './helper';
import { useNewUserOnboardingStore } from './store';

export const BeginSetup = () => {
  const { currentStateIndex, setCurrentStateIndex, setIsNewUserOnboardingComplete } = useNewUserOnboardingStore(
    'currentStateIndex',
    'setCurrentStateIndex',
    'setIsNewUserOnboardingComplete'
  );
  const { setOpen } = useVerticalSlideModal();

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: theme.spacing(2),
      }}
    >
      <iframe
        allowFullScreen
        css={{
          minHeight: 'calc(68vh - 38px);',
          width: 'calc(68vh * (16 / 9))', // Maintains 16:9 aspect
          border: '0px',
          pointerEvents: 'none',
        }}
        src={`https://fast.wistia.net/embed/iframe/izc4jzamjh?seo=false&videoFoam=true&controlsVisibleOnLoad=false&playbar=false&autoPlay=true`}
        title={t('Welcome to weave')}
      ></iframe>
      <div
        css={{
          display: 'flex',
          marginTop: theme.spacing(4),
          marginBottom: theme.spacing(1.25),
          justifyContent: 'center',
          alignItems: 'center',
          gap: theme.spacing(1.5),
        }}
      >
        <Button
          size='large'
          css={{ padding: `${theme.spacing(1)} ${theme.spacing(2)}` }}
          onClick={() => {
            setIsNewUserOnboardingComplete();
            pendo.enableGuides();
            setOpen(false);
          }}
          variant='secondary'
          trackingId='new-user-onboarding-skip-setup-click'
        >
          {t('Skip Setup')}
        </Button>
        <Button
          size='large'
          css={{ padding: `${theme.spacing(1)} ${theme.spacing(2)}` }}
          onClick={() => {
            setCurrentStateIndex(currentStateIndex + 1);
          }}
          trackingId='new-user-onboarding-begin-setup-click'
        >
          {t('Begin Setup')}
        </Button>
      </div>
    </div>
  );
};

export const ViewSelection = () => {
  const { currentStateIndex, userSelectedData, setCurrentStateIndex, setUserSelectedData } = useNewUserOnboardingStore(
    'currentStateIndex',
    'userSelectedData',
    'setCurrentStateIndex',
    'setUserSelectedData'
  );

  const userID = getUser()?.userID || '';
  const compactViewOnTop = isCompactViewOnTop(userID);

  interface ViewBlockProps {
    heading: string;
    description: string;
    btnText: string;
    btnAction: () => void;
  }

  useEffect(() => {
    pendo.track('new_user_onboarding_view_experiment', {
      userId: userID,
      viewOnTop: compactViewOnTop ? 'compact' : 'expanded',
      timestamp: new Date().toISOString(),
    });
  }, [userID]);

  const ViewBlock: React.FC<ViewBlockProps> = ({ heading, description, btnText, btnAction }) => {
    return (
      <div
        css={{
          maxWidth: '420px',
          background: theme.colors.neutral5,
          borderRadius: theme.spacing(1),
          padding: `${theme.spacing(2.5)} ${theme.spacing(2)}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Heading level={3} css={{ marginBottom: theme.spacing(2.5) }}>
          {heading}
        </Heading>
        <Text css={{ textAlign: 'center', marginBottom: theme.spacing(2.5) }}>{description}</Text>
        <Button
          size='large'
          onClick={btnAction}
          css={{ width: '100%' }}
          trackingId={`new-user-onboarding-${btnText.toLowerCase().replace(/\s+/g, '-')}-click`}
        >
          {btnText}
        </Button>
      </div>
    );
  };
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'row',
        gap: theme.spacing(2),
        height: 'calc(100% - 42px)',
        overflowY: 'clip',
      }}
    >
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '50%',
          gap: theme.spacing(3),
          justifyContent: 'space-between',
        }}
      >
        <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Heading css={{ textAlign: 'center' }}>Let’s get started!</Heading>
          <Heading level={3} css={{ textAlign: 'center', marginBottom: theme.spacing(5.5) }}>
            {t('How would you like to start using Weave today?')}
          </Heading>
          <div
            css={{
              display: 'flex',
              flexDirection: compactViewOnTop ? 'column' : 'column-reverse',
              gap: theme.spacing(2.5),
            }}
          >
            <ViewBlock
              heading={t('Expanded View')}
              description={t(
                'Use this version of Weave for complex workflows, like taking payments from appointments, reviewing forms, and exploring analytics. Best suited for those who want to multitask and use Weave like a pro.'
              )}
              btnText={t('Select Expanded View')}
              btnAction={() => {
                setUserSelectedData({ ...userSelectedData, view: 'expanded' });
                setCurrentStateIndex(currentStateIndex + 1);
              }}
            />
            <ViewBlock
              heading={t('Compact View')}
              description={t(
                'Use this version of Weave for workflows like sending texts or checking voicemails one-by-one. Best suited for those who need side-by-side access with their practice management systems.'
              )}
              btnText={t('Select Compact View')}
              btnAction={() => {
                setUserSelectedData({ ...userSelectedData, view: 'compact' });
                setCurrentStateIndex(currentStateIndex + 1);
              }}
            />
          </div>
        </div>
        <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div css={{ display: 'flex', gap: theme.spacing(2) }}>
            <Button
              size='large'
              variant='secondary'
              onClick={() => setCurrentStateIndex(currentStateIndex - 1)}
              trackingId='new-user-onboarding-back-to-begin-setup-click'
            >
              <Icon name='back-small' />
              <Text
                css={{ color: theme.colors.neutral70, marginLeft: theme.spacing(0.5), fontSize: theme.spacing(1.75) }}
              >
                {t('Back')}
              </Text>
            </Button>
            <Button
              size='large'
              variant='secondary'
              onClick={() => setCurrentStateIndex(currentStateIndex + 1)}
              trackingId='new-user-onboarding-back-to-begin-setup-click'
            >
              <Text
                css={{ color: theme.colors.neutral70, marginLeft: theme.spacing(0.5), fontSize: theme.spacing(1.75) }}
              >
                {t('Skip')}
              </Text>
              <Icon name='forward-small' />
            </Button>
          </div>
          <Text css={{ marginTop: theme.spacing(2), fontSize: theme.spacing(1.5), marginBottom: theme.spacing(5) }}>
            {t('Step 1 of 3')}
          </Text>
        </div>
      </div>
      <div
        css={{
          width: '50%',
          display: 'flex',
          alignItems: 'flex-start',
          background: theme.colors.primary20,
          marginRight: `-${theme.spacing(2)}`,
          marginTop: `-${theme.spacing(12)}`,
        }}
      >
        {' '}
        <iframe
          allowFullScreen
          css={{
            width: '100%',
            minWidth: '570px',
            height: '100%',
            border: '0px',
            pointerEvents: 'none',
          }}
          src={`https://fast.wistia.net/embed/iframe/r8fb1bj4k0?seo=false&videoFoam=true&controlsVisibleOnLoad=false&playbar=false&autoPlay=true&endVideoBehavior=loop`}
          title={t('Different Weave Views')}
        ></iframe>
      </div>
    </div>
  );
};

export const PopSelection = () => {
  const { currentStateIndex, userSelectedData, setCurrentStateIndex, setUserSelectedData } = useNewUserOnboardingStore(
    'currentStateIndex',
    'userSelectedData',
    'setCurrentStateIndex',
    'setUserSelectedData'
  );

  interface PopSelectionBlockProps {
    headingText: string;
    description: string;
    videoSrc: string;
    btnAction: () => void;
  }

  const PopSelectionBlock: React.FC<PopSelectionBlockProps> = ({ headingText, description, videoSrc, btnAction }) => {
    return (
      <div
        css={{
          maxWidth: '430px',
          padding: theme.spacing(3),
          background: theme.colors.neutral5,
          borderRadius: theme.spacing(1),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Heading level={3}>{headingText}</Heading>
        <Text css={{ textAlign: 'center', margin: `${theme.spacing(2.5)} 0` }}>{description}</Text>
        <video
          src={videoSrc}
          css={{ height: '100%', width: '100%', borderRadius: theme.spacing(1), overflow: 'hidden' }}
          autoPlay
          loop
          preload='none'
        />
        <Button
          size='large'
          css={{ width: '100%', marginTop: theme.spacing(2.5) }}
          onClick={btnAction}
          trackingId={`new-user-onboarding-${headingText.toLowerCase().replace(/\s+/g, '-')}-click`}
        >
          {t('Select')}
        </Button>
      </div>
    );
  };

  return (
    <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: theme.spacing(2) }}>
      <Heading level={3}>{t('How would you like to experience notifications?')}</Heading>
      <Text css={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(2) }}>
        {t('Don’t worry, you can change this later in your Settings.')}
      </Text>
      <div css={{ display: 'flex', gap: theme.spacing(3.75) }}>
        <PopSelectionBlock
          headingText={t('Display Until Dismissed')}
          description={t('Select this option to have your notifications stay on screen until you take action.')}
          videoSrc={displayUntilDismissedVideo}
          btnAction={() => {
            setUserSelectedData({ ...userSelectedData, notificationDisplayTime: 'display_until_dismissed' });
            setCurrentStateIndex(currentStateIndex + 1);
          }}
        />
        <PopSelectionBlock
          headingText={t('Display for 4 Seconds')}
          description={t('Select this option to have your notifications appear for 4 seconds, then leave your screen.')}
          videoSrc={fourSecondDismissalVideo}
          btnAction={() => {
            setUserSelectedData({ ...userSelectedData, notificationDisplayTime: '4_sec_dismissal' });
            setCurrentStateIndex(currentStateIndex + 1);
          }}
        />
      </div>
      <div css={{ marginTop: theme.spacing(4.75), display: 'flex', gap: theme.spacing(2) }}>
        <Button
          size='large'
          variant='secondary'
          onClick={() => setCurrentStateIndex(currentStateIndex - 1)}
          trackingId='new-user-onboarding-back-to-view-selection-click'
        >
          <Icon name='back-small' />
          <Text css={{ color: theme.colors.neutral70, marginLeft: theme.spacing(0.5), fontSize: theme.spacing(1.75) }}>
            {t('Back')}
          </Text>
        </Button>
        <Button
          size='large'
          variant='secondary'
          onClick={() => setCurrentStateIndex(currentStateIndex + 1)}
          trackingId='new-user-onboarding-skip-pop-selection-click'
        >
          <Text css={{ color: theme.colors.neutral70, marginLeft: theme.spacing(0.5), fontSize: theme.spacing(1.75) }}>
            {t('Skip')}
          </Text>
          <Icon name='forward-small' />
        </Button>
      </div>
      <Text css={{ fontSize: theme.spacing(1.5), marginTop: theme.spacing(2) }}>{t('Step 2 of 3')}</Text>
    </div>
  );
};

export const ThemeSelection = () => {
  const { currentStateIndex, userSelectedData, setCurrentStateIndex, setIsNewUserOnboardingComplete } =
    useNewUserOnboardingStore(
      'currentStateIndex',
      'userSelectedData',
      'setCurrentStateIndex',
      'setUserSelectedData',
      'setIsNewUserOnboardingComplete'
    );
  const { setOpen } = useVerticalSlideModal();
  const { colorObject } = useShellTheme('colorObject');
  const { setDurationMs } = useNotificationSettings();

  const handleSavePreferences = () => {
    shell?.invoke?.('main:update-options', { width: userSelectedData?.view === 'compact' ? 400 : 1500 });
    if (userSelectedData?.notificationDisplayTime === '4_sec_dismissal') {
      setDurationMs(4000);
    }
    saveShellThemeToLocalStorage(colorObject);
    setIsNewUserOnboardingComplete();
    pendo.enableGuides();
    setOpen(false);
  };

  return (
    <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: theme.spacing(2) }}>
      <Heading level={3}>{t('Pick your color theme.')}</Heading>
      <Text css={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(2) }}>
        {t('Don’t worry, you can change this later in your Settings.')}
      </Text>
      <ShellThemeSwitcher newUserOnboardingVersion />
      <div css={{ marginTop: theme.spacing(6), display: 'flex', gap: theme.spacing(2) }}>
        <Button
          size='large'
          variant='secondary'
          onClick={() => {
            setCurrentStateIndex(currentStateIndex - 1);
          }}
          trackingId='new-user-onboarding-back-to-pop-selection-click'
        >
          <Icon name='back-small' />
          {t('Back')}
        </Button>
        <Button
          size='large'
          css={{ width: '225px' }}
          onClick={handleSavePreferences}
          trackingId='new-user-onboarding-save-preferences-click'
        >
          {t('Save Preferences')}
        </Button>
      </div>
      <Text css={{ fontSize: theme.spacing(1.5), marginTop: theme.spacing(2) }}>{t('Step 3 of 3')}</Text>
    </div>
  );
};
